/**
 * celso-course-card.js
 * Comportamento interativo do card de curso — componente modular reutilizável.
 *
 * Expõe: window.CelsoCardUtils
 *   .applyCardState(cardEl)   — aplica estado (preço, cupom) a um card
 *   .applyCards(containerEl)  — aplica estado a todos os cards dentro de um container
 *   .setDefaultModalidades(containerEl) — ativa a primeira modalidade de cada card
 *
 * Delegação de eventos (automática):
 *   - Clique em .modalidade-btn  → troca modalidade ativa e atualiza preço/cupom
 *   - Clique em .midia-card-product → navega para a URL do produto
 */
(function () {
  'use strict';

  /* ── Helpers privados ──────────────────────────────────── */
  function hydrateShelfConfigsFromDom(container) {
    var root = container || document;
    var scripts = root.querySelectorAll('script[data-celso-shelf-config]');
    if (!scripts || !scripts.length) return;

    window.CelsoShelfConfigs = window.CelsoShelfConfigs || {};

    for (var i = 0; i < scripts.length; i++) {
      var script = scripts[i];
      if (!(script instanceof HTMLScriptElement)) continue;
      if (script.getAttribute('data-celso-shelf-config-hydrated') === 'true') continue;

      var raw = (script.textContent || '').trim();
      if (!raw) {
        script.setAttribute('data-celso-shelf-config-hydrated', 'true');
        continue;
      }

      try {
        var cfg = JSON.parse(raw);
        var pid = cfg && cfg.productId != null ? String(cfg.productId) : '';
        if (!pid) {
          script.setAttribute('data-celso-shelf-config-hydrated', 'true');
          continue;
        }
        window.CelsoShelfConfigs[pid] = cfg;
        if (window.EnrollPopupRegistry && typeof window.EnrollPopupRegistry.register === 'function') {
          window.EnrollPopupRegistry.register(cfg);
        } else {
          window.EnrollPopupPreConfigs = window.EnrollPopupPreConfigs || [];
          window.EnrollPopupPreConfigs.push(cfg);
        }
        script.setAttribute('data-celso-shelf-config-hydrated', 'true');
      } catch (error) {
        console.warn('Falha ao parsear config de card da coleção:', error);
      }
    }
  }

  var reapplyTimer = null;

  function scheduleReapply(delay) {
    if (reapplyTimer) clearTimeout(reapplyTimer);
    reapplyTimer = setTimeout(function () {
      reapplyTimer = null;
      hydrateShelfConfigsFromDom(document);
      if (window.CelsoCardUtils) {
        if (typeof window.CelsoCardUtils.setDefaultModalidades === 'function') {
          window.CelsoCardUtils.setDefaultModalidades(document);
        }
        if (typeof window.CelsoCardUtils.applyCards === 'function') {
          window.CelsoCardUtils.applyCards(document);
        }
      }
    }, typeof delay === 'number' ? delay : 80);
  }

  function hasCourseCardInNode(node) {
    if (!(node instanceof Element)) return false;
    if (node.matches('.course-card, .celso-card-scope, script[data-celso-shelf-config]')) return true;
    return Boolean(node.querySelector('.course-card, .celso-card-scope, script[data-celso-shelf-config]'));
  }

  function closestFromEventTarget(event, selector) {
    var target = event && event.target;
    if (target && target.nodeType === 3) {
      target = target.parentElement || target.parentNode;
    }

    if (target && typeof target.closest === 'function') {
      return target.closest(selector);
    }

    if (event && typeof event.composedPath === 'function') {
      var path = event.composedPath();
      for (var i = 0; i < path.length; i++) {
        var node = path[i];
        if (!node || node === document || node === window) continue;
        if (typeof node.matches === 'function' && node.matches(selector)) return node;
        if (typeof node.closest === 'function') {
          var match = node.closest(selector);
          if (match) return match;
        }
      }
    }

    return null;
  }

  function formatShelfMoney(cents) {
    var value = Number(cents) || 0;
    var formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value / 100);
    var parts = formatted.split(',');
    return {
      integer: (parts[0] || '0').replace(/\./g, ''),
      decimals: (parts[1] || '00').slice(0, 2),
    };
  }

  function getShelfCouponState(card, variantId, couponCodeBase) {
    if (!card || !variantId) return null;
    var selector = '[data-shelf-cupom-state][data-variant-id="' + String(variantId) + '"]';
    if (couponCodeBase) {
      selector += '[data-cupom-code-base="' + String(couponCodeBase) + '"]';
    }
    return card.querySelector(selector);
  }

  function shouldHideCouponBadges(card) {
    if (!card) return false;
    if (card.getAttribute && card.getAttribute('data-hide-coupon-badges') === 'true') return true;
    return Boolean(card.closest && card.closest('[data-hide-coupon-badges="true"]'));
  }

  function isPosEadActive(card) {
    if (!card || !card.classList.contains('course-card--pos')) return false;
    var btn = card.querySelector('.modalidade-btn.is-active') || card.querySelector('.modalidade-btn');
    if (!btn) return false;
    var mod = (btn.getAttribute('data-modalidade-option') || '').toLowerCase();
    return mod.indexOf('ead') !== -1 || mod.indexOf('distância') !== -1 || mod.indexOf('distancia') !== -1;
  }

  function getPosEadParcelas(card) {
    var n = parseInt(card.getAttribute('data-pos-ead-parcelas'), 10);
    return n > 0 ? n : 12;
  }

  function setShelfStandardPrice(card, cents, compareCents) {
    var posEad = isPosEadActive(card);
    var parcelas = posEad ? getPosEadParcelas(card) : 1;
    var displayCents = posEad ? Math.round(cents / parcelas) : cents;
    var formatted = formatShelfMoney(displayCents);
    var intEl = card.querySelector('.price-int');
    var centsEl = card.querySelector('.price-cents');
    var prefixEl = card.querySelector('.course-card__price-prefix');
    var installmentsEl = card.querySelector('.course-card__price-installments');
    if (intEl) intEl.textContent = formatted.integer;
    if (centsEl) centsEl.textContent = formatted.decimals;

    if (posEad) {
      card.classList.add('course-card--pos-ead-installments');
    } else {
      card.classList.remove('course-card--pos-ead-installments');
    }

    var isGraduacaoOrPos =
      card.classList.contains('course-card--graduacao') || card.classList.contains('course-card--pos');
    var isLivre = card.classList.contains('course-card--livre');
    var compareValue = Number(compareCents) || 0;

    if (compareValue > cents) {
      if (posEad) {
        var compareDisplay = compareValue;
        var formattedCompare = formatShelfMoney(compareDisplay);
        if (prefixEl) {
          prefixEl.innerHTML =
            'De <span class="price-prefix-currency">R$</span><span class="price-prefix-value">' +
            formattedCompare.integer +
            ',' +
            formattedCompare.decimals +
            '</span> por';
          prefixEl.style.display = 'block';
        }
        if (installmentsEl) {
          installmentsEl.textContent = parcelas + 'x';
          installmentsEl.style.display = 'block';
        }
        return;
      }
      var compareDisplay = posEad ? Math.round(compareValue / parcelas) : compareValue;
      var formattedCompare = formatShelfMoney(compareDisplay);
      if (prefixEl) {
        prefixEl.innerHTML =
          'De <span class="price-prefix-currency">R$</span><span class="price-prefix-value">' +
          formattedCompare.integer +
          ',' +
          formattedCompare.decimals +
          '</span> por';
        prefixEl.style.display = 'block';
      }
      if (installmentsEl) {
        installmentsEl.style.display = 'none';
      }
      return;
    }

    if (installmentsEl) {
      if (posEad) {
        installmentsEl.textContent = parcelas + 'x';
        installmentsEl.style.display = 'block';
      } else {
        installmentsEl.style.display = 'none';
      }
    }

    if (posEad && prefixEl) {
      prefixEl.style.display = 'none';
    } else if (isGraduacaoOrPos && prefixEl) {
      prefixEl.innerHTML = 'A partir de';
      prefixEl.style.display = 'block';
    } else if (isLivre && prefixEl) {
      prefixEl.innerHTML = '';
      prefixEl.style.display = 'none';
    } else if (prefixEl) {
      prefixEl.style.display = 'none';
    }
  }

  function setShelfProPrice(card, cents) {
    var formatted = formatShelfMoney(cents);
    var ei = card.querySelector('.course-card__button-price-integer');
    var ed = card.querySelector('.course-card__button-price-decimals');
    if (ei) ei.textContent = formatted.integer;
    if (ed) ed.textContent = ',' + formatted.decimals;
  }

  function setShelfBadge(card, badge, isVisible, firstCents, codeBase) {
    if (!badge) return;
    if (shouldHideCouponBadges(card)) {
      isVisible = false;
    }
    if (isVisible) {
      var C = window.CelsoCupom;
      var firstFormatted = C && typeof C.formatarDinheiro === 'function'
        ? C.formatarDinheiro(firstCents)
        : (formatShelfMoney(firstCents).integer + ',' + formatShelfMoney(firstCents).decimals);
      var priceEl = badge.querySelector('.celso-discount-badge-price');
      var codeEl = badge.querySelector('.celso-discount-badge-code');
      var midEl = badge.querySelector('.celso-discount-badge__mid');
      if (priceEl) priceEl.textContent = firstFormatted;
      if (codeEl) codeEl.textContent = codeBase || '';
      if (midEl) midEl.style.display = '';
      badge.removeAttribute('hidden');
      badge.hidden = false;
      badge.style.display = '';
      badge.style.visibility = '';
      badge.setAttribute('aria-hidden', 'false');
      card.setAttribute('data-has-cupom', 'true');
    } else {
      badge.hidden = true;
      badge.setAttribute('hidden', '');
      badge.setAttribute('aria-hidden', 'true');
      card.removeAttribute('data-has-cupom');
    }
  }

  function atualizarDadosMatricula(modalidade) {
    if (typeof window === 'undefined' || !modalidade) return;
    var dados = {};
    try {
      var raw = localStorage.getItem('dadosMatricula');
      var parsed = raw ? JSON.parse(raw) : {};
      if (parsed && typeof parsed === 'object') dados = parsed;
    } catch (error) {
      console.warn('Não foi possível ler dados de matrícula salvos:', error);
    }

    if (dados.modalidade === modalidade) return;
    dados.modalidade = modalidade;

    try {
      localStorage.setItem('dadosMatricula', JSON.stringify(dados));
    } catch (error) {
      console.warn('Não foi possível atualizar dados de matrícula:', error);
    }

    try {
      window.dispatchEvent(new CustomEvent('dadosMatriculaAtualizados', { detail: { dados: dados } }));
    } catch (error) {
      console.warn('Não foi possível notificar atualização de matrícula:', error);
    }
  }

  function salvarModalidadeSelecionada(valor, productId) {
    if (!valor) return;
    try {
      localStorage.setItem('modalidadeSelecionada', valor);
    } catch (error) {
      console.warn('Não foi possível salvar a modalidade selecionada:', error);
    }
    try {
      window.dispatchEvent(new CustomEvent('modalidadeSelecionada', {
        detail: { value: valor, source: 'card', productId: productId }
      }));
    } catch (error) {
      console.warn('Não foi possível emitir evento de modalidade selecionada:', error);
    }
  }

  function findVariantInShelfCfg(cfg, variantId) {
    if (!cfg || !cfg.variants) return null;
    var id = String(variantId);
    for (var i = 0; i < cfg.variants.length; i++) {
      if (String(cfg.variants[i].id) === id) return cfg.variants[i];
    }
    return null;
  }

  function getActiveVariantForShelfCard(card) {
    var pid = card.getAttribute('data-product-id');
    var cfg = window.CelsoShelfConfigs && pid && window.CelsoShelfConfigs[String(pid)];
    if (!cfg || !cfg.variants || !cfg.variants.length) return null;
    if (card.getAttribute('data-shelf-card-type') === 'pro') {
      return cfg.variants[0];
    }
    var btn = card.querySelector('.modalidade-btn.is-active') || card.querySelector('.modalidade-btn');
    if (!btn || !btn.dataset.variantId) return cfg.variants[0];
    return findVariantInShelfCfg(cfg, btn.dataset.variantId) || cfg.variants[0];
  }

  function resolveShelfDisplayCents(variant, card) {
    var priceCents = Number(variant && variant.price) || 0;
    var compareCents = Number(variant && variant.compare_at_price) || 0;
    var result = {
      priceCents: priceCents,
      compareCents: compareCents,
      hasCoupon: false,
      resolved: null,
      firstCents: null,
      couponCodeBase: '',
    };

    var C = window.CelsoCupom;
    if (shouldHideCouponBadges(card)) return result;
    if (!variant || !C) return result;

    var pid = card && card.getAttribute ? card.getAttribute('data-product-id') : '';
    var cfg = window.CelsoShelfConfigs && pid && window.CelsoShelfConfigs[String(pid)];
    var couponOptions = {
      allowImplicit: true,
      candidates: typeof C.getCouponCandidates === 'function' ? (C.getCouponCandidates() || []) : [],
    };

    var resolved =
      typeof C.resolveEffectiveCouponForVariant === 'function'
        ? C.resolveEffectiveCouponForVariant(variant, couponOptions)
        : typeof C.resolveBestCouponForVariant === 'function'
          ? C.resolveBestCouponForVariant(variant, couponOptions.candidates, couponOptions)
          : null;

    if (!resolved || !resolved.entry) return result;

    result.hasCoupon = true;
    result.resolved = resolved;
    result.couponCodeBase =
      resolved.displayCode ||
      (typeof C.getCupomBase === 'function' ? C.getCupomBase(resolved.code || '') : resolved.code || '');

    if (card) {
      var state = getShelfCouponState(card, variant.id, result.couponCodeBase);
      if (state) {
        var first = Number(state.dataset.firstCents);
        if (Number.isFinite(first) && first >= 0) {
          result.firstCents = first;
        }
      }
    }

    if (!result.firstCents && typeof C.resolverValorPrimeiraMensalidade === 'function') {
      var primeira = C.resolverValorPrimeiraMensalidade(variant, resolved.entry);
      if (primeira && Number.isFinite(primeira.valorCentavos)) {
        result.firstCents = primeira.valorCentavos;
      }
    }

    var ratioFn =
      typeof C.resolverRatioPrecoExibicaoCupom === 'function'
        ? C.resolverRatioPrecoExibicaoCupom
        : null;
    if (ratioFn) {
      var ratioInfo = ratioFn(resolved.entry);
      var base =
        typeof C.obterPrecoBaseDaVariante === 'function'
          ? Number(C.obterPrecoBaseDaVariante(variant))
          : priceCents;
      if (ratioInfo && ratioInfo.ratio > 0 && ratioInfo.ratio < 1 && Number.isFinite(base) && base > 0) {
        result.priceCents = Math.round(base * ratioInfo.ratio);
      }
    }

    if (!Number.isFinite(result.priceCents) || result.priceCents <= 0) {
      result.priceCents = priceCents;
    }

    return result;
  }

  function applyShelfCardState(card) {
    var variant = getActiveVariantForShelfCard(card);
    if (!variant) return;

    var badge = card.querySelector('.celso-discount-badge--shelf');
    var isPro = card.getAttribute('data-shelf-card-type') === 'pro';
    var posEad = isPosEadActive(card);

    if (shouldHideCouponBadges(card)) {
      setShelfBadge(card, badge, false);
      if (isPro) {
        setShelfProPrice(card, Number(variant.price) || 0);
      } else {
        setShelfStandardPrice(card, Number(variant.price) || 0, Number(variant.compare_at_price) || 0);
      }
      return;
    }

    var display = resolveShelfDisplayCents(variant, card);

    if (display.hasCoupon) {
      var shelfPriceCents = display.priceCents;
      var variantPrice = Number(variant.price) || 0;
      if (!Number.isFinite(shelfPriceCents) || shelfPriceCents <= 0) {
        shelfPriceCents = variantPrice;
      }
      if (display.firstCents) {
        setShelfBadge(card, badge, true, display.firstCents, display.couponCodeBase);
      } else if (posEad && shelfPriceCents > 0 && shelfPriceCents < variantPrice) {
        badge.hidden = false;
        badge.removeAttribute('hidden');
        badge.setAttribute('aria-hidden', 'false');
        card.setAttribute('data-has-cupom', 'true');
      } else {
        setShelfBadge(card, badge, false);
      }
      if (isPro) {
        setShelfProPrice(card, shelfPriceCents);
      } else {
        setShelfStandardPrice(card, shelfPriceCents, display.compareCents);
      }
      return;
    }

    setShelfBadge(card, badge, false);
    if (isPro) {
      setShelfProPrice(card, Number(variant.price) || 0);
    } else {
      setShelfStandardPrice(card, Number(variant.price) || 0, Number(variant.compare_at_price) || 0);
    }
  }

  /* ── API pública ───────────────────────────────────────── */

  window.CelsoCardUtils = {
    applyCardState: applyShelfCardState,

    applyCards: function (container) {
      var root = container || document;
      root.querySelectorAll('.course-card').forEach(function (c) {
        applyShelfCardState(c);
      });
    },

    setDefaultModalidades: function (container) {
      var root = container || document;
      root.querySelectorAll('.course-card').forEach(function (card) {
        var firstBtn = card.querySelector('.modalidade-btn');
        if (!firstBtn) return;
        card.querySelectorAll('.modalidade-btn').forEach(function (b) {
          b.classList.remove('is-active');
        });
        firstBtn.classList.add('is-active');
      });
    }
  };

  /* ── Delegação de eventos ──────────────────────────────── */

  /* ── Auto-init ─────────────────────────────────────────── */
  function autoInit() {
    hydrateShelfConfigsFromDom(document);
    window.CelsoCardUtils.setDefaultModalidades(document);
    window.CelsoCardUtils.applyCards(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', autoInit);
  } else {
    autoInit();
  }

  if (!window.__celsoCourseCardFilterUpdateHandlerBound) {
    window.__celsoCourseCardFilterUpdateHandlerBound = true;
    document.addEventListener('filter:update', function () {
      // O evento dispara antes do fetch/morph da section; reagendamos para depois.
      scheduleReapply(240);
      scheduleReapply(600);
    });
    document.addEventListener('facets:updated', function () {
      // Shopify Facets AJAX também atualiza o DOM com novos cards.
      scheduleReapply(240);
      scheduleReapply(600);
    });
  }

  if (!window.__celsoCourseCardSectionLoadHandlerBound) {
    window.__celsoCourseCardSectionLoadHandlerBound = true;
    document.addEventListener('shopify:section:load', function () {
      scheduleReapply(60);
    });
  }

  if (!window.__celsoCourseCardMutationObserverBound) {
    window.__celsoCourseCardMutationObserverBound = true;
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (hasCourseCardInNode(added[j])) {
            scheduleReapply(60);
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (!window.__celsoCourseCardClickHandlerBound) {
    window.__celsoCourseCardClickHandlerBound = true;
    document.addEventListener('click', function (e) {
      /* Clique no botao PRO -> abrir modal de matricula */
      var proButton = closestFromEventTarget(e, '.course-card__button-pro');
      if (proButton) {
        var card = proButton.closest('.course-card');
        var productId = proButton.getAttribute('data-product-id') || (card && card.getAttribute ? card.getAttribute('data-product-id') : null);
        if (!productId && proButton.tagName && proButton.tagName.toLowerCase() === 'a') return;
        e.preventDefault();
        if (window.EnrollPopupRegistry && typeof window.EnrollPopupRegistry.open === 'function') {
          window.EnrollPopupRegistry.open(productId);
          return;
        }
        if (typeof window.abrirPopupMatricula === 'function') {
          window.abrirPopupMatricula(productId);
          return;
        }
        return;
      }

      /* Clique no botao de compra (graduacao/pos/livre) -> abrir modal */
      var ctaButton = closestFromEventTarget(e, '.course-card__cta');
      if (ctaButton) {
        var cardEl = ctaButton.closest('.course-card');
        var pid = ctaButton.getAttribute('data-product-id') || (cardEl && cardEl.getAttribute ? cardEl.getAttribute('data-product-id') : null);
        if (!pid && ctaButton.tagName && ctaButton.tagName.toLowerCase() === 'a') return;
        e.preventDefault();

        var activeModalidadeBtn = cardEl ? cardEl.querySelector('.modalidade-btn.is-active') : null;
        var modalidadeValue = activeModalidadeBtn ? activeModalidadeBtn.dataset.modalidadeOption : '';

        if (ctaButton.dataset.modalidadeRequired === 'true' && !modalidadeValue) {
          return;
        }

        if (modalidadeValue) {
          salvarModalidadeSelecionada(modalidadeValue, pid);
          atualizarDadosMatricula(modalidadeValue);
        }

        if (window.EnrollPopupRegistry && typeof window.EnrollPopupRegistry.open === 'function') {
          window.EnrollPopupRegistry.open(pid);
          return;
        }
        if (typeof window.abrirPopupMatricula === 'function') {
          window.abrirPopupMatricula(pid);
          return;
        }
        return;
      }
      /* Clique em botão de modalidade */
      var btn = closestFromEventTarget(e, '.modalidade-btn');
      if (btn) {
        var modalidadeCard = btn.closest('.course-card');
        if (!modalidadeCard) return;
        modalidadeCard.querySelectorAll('.modalidade-btn').forEach(function (b) {
          b.classList.remove('is-active');
        });
        btn.classList.add('is-active');
        applyShelfCardState(modalidadeCard);
        return;
      }

      /* Clique no card (midia-card-product) navega para o produto */
      var midiaCard = closestFromEventTarget(e, '.midia-card-product');
      if (midiaCard) {
        var url = midiaCard.dataset.productUrl;
        if (!url) return;
        if (closestFromEventTarget(e, 'button') || closestFromEventTarget(e, 'a')) return;
        e.preventDefault();
        window.location.href = url;
      }
    });
  }
})();
